import _ from 'underscore';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { Status } from '@bingads-webui-campaign-react/diagnostic-card';
import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';

import { statusAndActionReport, auditPointDisplayed, getFullProductUrl } from './utils';
import { DetailCardsKey } from '../const';

const STATUS_TEXT = {
  InReview: _TL_('In review'),
  Rejected: _TL_('Rejected'),
  Approved: _TL_('Approved'),
  Limited: _TL_('Limited'),
};


const getCardDetails = ({
  auditPoint, scope, i18n, activity, locale, campaignType,
}) => {
  const isCampaignTypeApp = (campaignType && campaignType === 'App') ?? false;

  const onCardButtonClicked = () => {
    statusAndActionReport({
      activity,
      type: 'editorial',
      action: 'navigate to asset groups page',
      details: `AuditPoint: ${auditPoint}`,
    });

    if (!isSDK()) {
      const assetGroupUrl = isCampaignTypeApp ? getRoutePathById(allSiteMap, 'appads-asset-groups') : getRoutePathById(allSiteMap, 'asset-groups');
      scope.save(assetGroupUrl);
    } else {
      const url = getFullProductUrl('asset-groups', scope, ['campaignId']);
      window.open(url);
    }
  };

  auditPointDisplayed({ activity, auditPoint, area: 'Editorial' });

  switch (auditPoint) {
    case 'AllAssetGroupsEditorialRejected':
    case 'AllAssetGroupsEditorialAppealRejected':
    case 'AllAssetGroupsEditorialRejectedOrAppealRejected':
      return {
        cardTitle: i18n.getString(_TL_('All assets rejected')),
        cardMsg: i18n.getString(_TL_('None of your assets meet our requirements. Please check and fix them.')),
        onCardButtonClicked,
        cardButtonText: i18n.getString(_TL_('Fix assets')),
      };

    case 'AllAssetGroupsEditorialLimited':
      return {
        status: Status.Warning,
        statusLabelMsg: i18n.getString(_TL_('Approved limited')),
        cardMsg: i18n.getString(_TL_('The asset groups have been approved in at least one of your targeted locations, but have also been disapproved in at least one other targeted location.')),
        learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/52302/1`,
        cardTitle: i18n.getString(_TL_('Asset groups editorial limited')),
        onCardButtonClicked,
        cardButtonText: i18n.getString(_TL_('Fix assets')),
      };

    case 'NoAssetGroups':
      return {
        statusLabelMsg: i18n.getString(_TL_('No asset group')),
        cardTitle: i18n.getString(_TL_('There are no asset groups in this campaign')),
        cardMsg: i18n.getString(_TL_('Looks like your campaign doesn\'t have asset groups yet. Create at least one asset group to get started.')),
        onCardButtonClicked,
        cardButtonText: i18n.getString(_TL_('Fix now')),
        learnMoreLink: isCampaignTypeApp ? `https://help.ads.microsoft.com/#apex/ads/${locale}/60350/0/#TechnicalRequirements` : `https://help.ads.microsoft.com/#apex/ads/${locale}/60176/0`,
      };

    case 'NoActiveAssetGroups':
      return {
        statusLabelMsg: i18n.getString(_TL_('No active asset group')),
        cardTitle: i18n.getString(_TL_('There are no active asset groups in this campaign')),
        cardMsg: i18n.getString(_TL_('Your campaign did not perform because there are no active asset groups. To receive clicks and conversions, add an active asset group.')),
        onCardButtonClicked,
        cardButtonText: i18n.getString(_TL_('Fix now')),
        learnMoreLink: isCampaignTypeApp ? `https://help.ads.microsoft.com/#apex/ads/${locale}/60350/0/#TechnicalRequirements` : `https://help.ads.microsoft.com/#apex/ads/${locale}/60176/0`,
      };

    case 'AllAssetGroupsEditorialUnderAppeal':
    case 'AllAssetGroupsEditorialUnderAppealOrPending':
      return {
        cardMsg: i18n.getString(_TL_('One or multiple asset groups in this campaign are under editorial review.')),
      };

    case 'AllAssetGroupsEditorialApproved':
    case 'AllAssetGroupsEditorialPending':
      return {};


    case 'AllAdGroupsEditorialRejected':
    case 'AllAdGroupsEditorialAppealRejected':
    case 'AllAdGroupsEditorialRejectedOrAppealRejected':
      return {
        cardTitle: i18n.getString(_TL_('All ad groups rejected')),
        cardMsg: i18n.getString(_TL_('None of your ad groups meet our requirements. Please check and fix them.')),
        onCardButtonClicked,
        cardButtonText: i18n.getString(_TL_('Fix ad groups')),
      };

    case 'AllAdGroupsEditorialLimited':
      return {
        status: Status.Warning,
        statusLabelMsg: i18n.getString(_TL_('Approved limited')),
        cardMsg: i18n.getString(_TL_('The ad groups have been approved in at least one of your targeted locations, but have also been disapproved in at least one other targeted location.')),
        learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/52302/1`,
        cardTitle: i18n.getString(_TL_('Ad groups editorial limited')),
        onCardButtonClicked,
        cardButtonText: i18n.getString(_TL_('Fix ad groups')),
      };

    case 'NoAdGroups':
      return {
        statusLabelMsg: i18n.getString(_TL_('No ad group')),
        cardTitle: i18n.getString(_TL_('There are no ad groups in this campaign')),
        cardMsg: i18n.getString(_TL_('Looks like your campaign doesn\'t have ad groups yet. Create at least one ad group to get started.')),
        onCardButtonClicked,
        cardButtonText: i18n.getString(_TL_('Fix now')),
        learnMoreLink: isCampaignTypeApp ? `https://help.ads.microsoft.com/#apex/ads/${locale}/60350/0/#TechnicalRequirements` : `https://help.ads.microsoft.com/#apex/ads/${locale}/60176/0`,
      };

    case 'NoActiveAdGroups':
      return {
        statusLabelMsg: i18n.getString(_TL_('No active ad group')),
        cardTitle: i18n.getString(_TL_('There are no active ad groups in this campaign')),
        cardMsg: i18n.getString(_TL_('Your campaign did not perform because there are no active ad groups. To receive clicks and conversions, add an active ad group.')),
        onCardButtonClicked,
        cardButtonText: i18n.getString(_TL_('Fix now')),
        learnMoreLink: isCampaignTypeApp ? `https://help.ads.microsoft.com/#apex/ads/${locale}/60350/0/#TechnicalRequirements` : `https://help.ads.microsoft.com/#apex/ads/${locale}/60176/0`,
      };

    case 'AllAdGroupsEditorialUnderAppeal':
    case 'AllAdGroupsEditorialUnderAppealOrPending':
      return {
        cardMsg: i18n.getString(_TL_('One or multiple ad groups in this campaign are under editorial review.')),
      };

    case 'AllAdGroupsEditorialApproved':
    case 'AllAdGroupsEditorialPending':
      return {};

    default:
      activity.error(`Audit point mapping not found in getEditorialStatus:${auditPoint}`);
      return {};
  }
};

export const getEditorialStatus = ({
  details, scope, i18n, activity, locale, campaignType,
}) => {
  if (_.isEmpty(details)
    || _.isEmpty(_.get(details, 'Value'))
    || _.get(details, ['Value', 'ErrorCode'], 0) !== 0
    || _.get(details, ['Value', 'ErrorMessage'])) {
    statusAndActionReport({
      activity,
      type: 'editorial',
      details: `Error - details: ${JSON.stringify(details)}`,
    });
    return {
      name: i18n.getString(_TL_('Editorial')),
      key: DetailCardsKey.editorial,
    };
  }

  const status = _.get(details, ['Value', 'Status']);
  const auditPoint = _.get(details, ['Value', 'AuditPoints', 0]);

  const cardDetails = getCardDetails({
    auditPoint, scope, i18n, activity, locale, campaignType,
  });
  let statusIcon = Status.Error;
  if (status === 'Approved') {
    statusIcon = Status.Active;
  } else if (status === 'InReview') {
    statusIcon = Status.Pending;
  }

  return {
    name: i18n.getString(_TL_('Editorial')),
    key: DetailCardsKey.editorial,

    status: statusIcon,
    statusLabelMsg: i18n.getString(_.get(STATUS_TEXT, status, _TL_(''))),

    ...cardDetails,
  };
};
